import React, {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  type SelectDateRangeState,
  dateRange30DaysAgo,
} from "../../../../components/SelectDateRange/SelectDateRange";
import { FilterValue } from "../../../graphql";
import { PositionOptions } from "../skills-report/builder/SkillsReportPositionSelect";

export type DateRangeState = SelectDateRangeState;

export type DurationRangeState = {
  minSeconds: number;
  maxSeconds: number;
};

export type InterviewTypeFilter = "screen" | "core" | null;

export type MaxInterviewCountOption = 50 | 100 | 200;

export type FilterState = {
  callNameSearch: string;
  dateRange: DateRangeState;
  selectedPositions: PositionOptions;
  selectedInterviewers: FilterValue[];
  selectedDepartments: FilterValue[];
  selectedStages: FilterValue[];
  selectedLocations: FilterValue[];
  selectedCandidateOutcomes: FilterValue[];
  durationRange: { minSeconds: number; maxSeconds: number };
  interviewType: InterviewTypeFilter;
  maxInterviewCount: MaxInterviewCountOption;
};

export type UseCustomInsightsBuilderReturn = FilterState & {
  setCallNameSearch: Dispatch<SetStateAction<string>>;
  setDateRange: Dispatch<SetStateAction<DateRangeState>>;
  setSelectedPositions: Dispatch<SetStateAction<PositionOptions>>;
  setSelectedInterviewers: Dispatch<SetStateAction<FilterValue[]>>;
  setSelectedDepartments: Dispatch<SetStateAction<FilterValue[]>>;
  setSelectedStages: Dispatch<SetStateAction<FilterValue[]>>;
  setSelectedLocations: Dispatch<SetStateAction<FilterValue[]>>;
  setSelectedCandidateOutcomes: Dispatch<SetStateAction<FilterValue[]>>;
  setDurationRange: Dispatch<SetStateAction<DurationRangeState>>;
  setInterviewType: Dispatch<SetStateAction<InterviewTypeFilter>>;
  setMaxInterviewCount: Dispatch<SetStateAction<MaxInterviewCountOption>>;
};

const defaultDateRange: DateRangeState = dateRange30DaysAgo();

const defaultDurationRange: DurationRangeState = {
  minSeconds: 120,
  maxSeconds: 7200,
};

export const useCustomInsightsFilters = (
  defaultFilters: Partial<FilterState> = {}
): UseCustomInsightsBuilderReturn => {
  const [callNameSearch, setCallNameSearch] = useState(
    defaultFilters.callNameSearch || ""
  );
  const [dateRange, setDateRange] = useState<DateRangeState>(
    defaultFilters.dateRange || defaultDateRange
  );
  const [selectedPositions, setSelectedPositions] = useState<PositionOptions>(
    defaultFilters.selectedPositions || []
  );
  const [selectedInterviewers, setSelectedInterviewers] = useState<
    FilterValue[]
  >(defaultFilters.selectedInterviewers || []);
  const [selectedDepartments, setSelectedDepartments] = useState<FilterValue[]>(
    defaultFilters.selectedDepartments || []
  );
  const [selectedStages, setSelectedStages] = useState<FilterValue[]>(
    defaultFilters.selectedStages || []
  );
  const [selectedLocations, setSelectedLocations] = useState<FilterValue[]>(
    defaultFilters.selectedLocations || []
  );
  const [selectedCandidateOutcomes, setSelectedCandidateOutcomes] = useState<
    FilterValue[]
  >(defaultFilters.selectedCandidateOutcomes || []);
  const [durationRange, setDurationRange] = useState<DurationRangeState>(
    defaultFilters.durationRange || defaultDurationRange
  );
  const [interviewType, setInterviewType] = useState<InterviewTypeFilter>(
    defaultFilters.interviewType || null
  );
  const [maxInterviewCount, setMaxInterviewCount] =
    useState<MaxInterviewCountOption>(defaultFilters.maxInterviewCount || 50);

  return {
    callNameSearch,
    setCallNameSearch,
    dateRange,
    setDateRange,
    selectedPositions,
    setSelectedPositions,
    selectedInterviewers,
    setSelectedInterviewers,
    selectedDepartments,
    setSelectedDepartments,
    selectedStages,
    setSelectedStages,
    selectedLocations,
    setSelectedLocations,
    selectedCandidateOutcomes,
    setSelectedCandidateOutcomes,
    durationRange,
    setDurationRange,
    interviewType,
    setInterviewType,
    maxInterviewCount,
    setMaxInterviewCount,
  };
};

type CustomInsightsFiltersContextType = UseCustomInsightsBuilderReturn & {
  resetFilters: () => void;
  setAllFilters: (state: Partial<FilterState>) => void;
};

const CustomInsightsFiltersContext =
  createContext<CustomInsightsFiltersContextType | null>(null);

export default function CustomInsightsFiltersProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const {
    callNameSearch,
    dateRange,
    selectedPositions,
    selectedInterviewers,
    selectedDepartments,
    selectedStages,
    selectedLocations,
    selectedCandidateOutcomes,
    durationRange,
    interviewType,
    maxInterviewCount,
    setCallNameSearch,
    setDateRange,
    setSelectedPositions,
    setSelectedInterviewers,
    setSelectedDepartments,
    setSelectedStages,
    setSelectedLocations,
    setSelectedCandidateOutcomes,
    setDurationRange,
    setInterviewType,
    setMaxInterviewCount,
  } = useCustomInsightsFilters();
  const resetFilters = useCallback(() => {
    setCallNameSearch("");
    setDateRange(defaultDateRange);
    setSelectedPositions([]);
    setSelectedInterviewers([]);
    setSelectedDepartments([]);
    setSelectedStages([]);
    setSelectedLocations([]);
    setSelectedCandidateOutcomes([]);
    setDurationRange(defaultDurationRange);
    setInterviewType(null);
    setMaxInterviewCount(50);
  }, [
    setCallNameSearch,
    setDateRange,
    setSelectedPositions,
    setSelectedInterviewers,
    setSelectedDepartments,
    setSelectedStages,
    setSelectedLocations,
    setSelectedCandidateOutcomes,
    setDurationRange,
    setInterviewType,
    setMaxInterviewCount,
  ]);

  const setAllFilters = useCallback(
    (state: Partial<FilterState>) => {
      if (state.callNameSearch !== undefined)
        setCallNameSearch(state.callNameSearch);
      if (state.dateRange !== undefined) setDateRange(state.dateRange);
      if (state.selectedPositions !== undefined)
        setSelectedPositions(state.selectedPositions);
      if (state.selectedInterviewers !== undefined)
        setSelectedInterviewers(state.selectedInterviewers);
      if (state.selectedDepartments !== undefined)
        setSelectedDepartments(state.selectedDepartments);
      if (state.selectedStages !== undefined)
        setSelectedStages(state.selectedStages);
      if (state.selectedLocations !== undefined)
        setSelectedLocations(state.selectedLocations);
      if (state.selectedCandidateOutcomes !== undefined)
        setSelectedCandidateOutcomes(state.selectedCandidateOutcomes);
      if (state.durationRange !== undefined)
        setDurationRange(state.durationRange);
      if (state.interviewType !== undefined)
        setInterviewType(state.interviewType);
      if (state.maxInterviewCount !== undefined)
        setMaxInterviewCount(state.maxInterviewCount);
    },
    [
      setCallNameSearch,
      setDateRange,
      setSelectedPositions,
      setSelectedInterviewers,
      setSelectedDepartments,
      setSelectedStages,
      setSelectedLocations,
      setSelectedCandidateOutcomes,
      setDurationRange,
      setInterviewType,
      setMaxInterviewCount,
    ]
  );

  const value = useMemo(
    () => ({
      callNameSearch,
      dateRange,
      selectedPositions,
      selectedInterviewers,
      selectedDepartments,
      selectedStages,
      selectedLocations,
      selectedCandidateOutcomes,
      durationRange,
      interviewType,
      maxInterviewCount,
      setCallNameSearch,
      setDateRange,
      setSelectedPositions,
      setSelectedInterviewers,
      setSelectedDepartments,
      setSelectedStages,
      setSelectedLocations,
      setSelectedCandidateOutcomes,
      setDurationRange,
      setInterviewType,
      setMaxInterviewCount,
      resetFilters,
      setAllFilters,
    }),
    [
      callNameSearch,
      dateRange,
      selectedPositions,
      selectedInterviewers,
      selectedDepartments,
      selectedStages,
      selectedLocations,
      selectedCandidateOutcomes,
      durationRange,
      interviewType,
      maxInterviewCount,
      setCallNameSearch,
      setDateRange,
      setSelectedPositions,
      setSelectedInterviewers,
      setSelectedDepartments,
      setSelectedStages,
      setSelectedLocations,
      setSelectedCandidateOutcomes,
      setDurationRange,
      setInterviewType,
      setMaxInterviewCount,
      resetFilters,
      setAllFilters,
    ]
  );
  return (
    <CustomInsightsFiltersContext.Provider value={value}>
      {children}
    </CustomInsightsFiltersContext.Provider>
  );
}

export function useCustomInsightsFiltersContext(): CustomInsightsFiltersContextType {
  const ctx = useContext(CustomInsightsFiltersContext);
  if (!ctx)
    throw new Error(
      "useCustomInsightsFiltersContext must be used within CustomInsightsFiltersProvider"
    );
  return ctx;
}
